import React, { useEffect, useState } from "react";

import { Row, Col, Card, Skeleton } from "antd";
import { CheckSquareOutlined, ExclamationCircleOutlined, StopOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { getLicences } from "../../API/licences";

import WithPermission from "../Common/WithPermission/WithPermission";
import PageTitle from "../Common/PageTitle/PageTitle";

import CompanyCard from "./components/CompanyCard/CompanyCard";
import LicenceCard from "./components/LicenceCard/LicenceCard";
import StatusCard from "./components/StatusCard/StatusCard";

import { useAuth } from "../Context/AuthContext";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "./Licences.css";

dayjs.extend(isSameOrAfter);

const Licences = () => {
  const { userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;

  const [loading, setLoading] = useState(true);
  const [licences, setLicences] = useState([]);

  useEffect(() => {
    const fetchLicences = async () => {
      try {
        setLoading(true);
        const data = await getLicences();
        setLicences(data || []);
      } catch (error) {
        console.error("Error al obtener licencias:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLicences();
  }, []);

  const calculateRemainingDays = (date) => {
    if (!date) return null;
    const today = dayjs().startOf("day");
    const targetDate = dayjs(date).startOf("day");
    const diff = targetDate.diff(today, "day");
    return diff >= 0 ? diff : 0;
  };

  const formatRemainingDaysText = (days, label) => {
    if (days === null) return null;
    if (days === 0) return `El ${label.replace("el ", "").trim()} vence hoy`;
    if (days === 1) return `Falta 1 día para ${label}`;
    return `Faltan ${days} días para ${label}`;
  };

  const getStatusProps = (statusName) => {
    switch (statusName) {
      case "Activo":
        return { color: "success", icon: <CheckSquareOutlined /> };
      case "Pendiente de Pago":
        return { color: "warning", icon: <ExclamationCircleOutlined /> };
      case "Suspendido":
        return { color: "error", icon: <StopOutlined /> };
      case "Baja":
        return { color: "blue", icon: <MinusCircleOutlined /> };
      default:
        return { color: "default", icon: <MinusCircleOutlined /> };
    }
  };

  return (
    <WithPermission isSuperuser={isSuperuser} requiredPermission="read" redirectTo="/home">
      <>
        <Row justify="start" className="licences-row">
          <Col>
            <PageTitle title="Licencias" icon="licences" />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Col xs={24} key={i}>
                <Card bordered={false} className="licences-card">
                  <Skeleton active paragraph={{ rows: 10 }} />
                </Card>
              </Col>
            ))
          ) : licences.length === 0 ? (
            <Col span={24} style={{ textAlign: "center", marginTop: 40 }}>
              <p>No se encontraron licencias disponibles.</p>
            </Col>
          ) : (
            licences.map((licence) => {
              const product = licence.product || {};
              const payment = licence.payment_method || {};
              const status = licence.status || {};
              const statusProps = getStatusProps(status.name);
              console.log("statusProps", statusProps)

              const daysUntilNextPayment = calculateRemainingDays(licence.next_payment_date);
              const daysUntilExpiry = calculateRemainingDays(licence.defeated_date);

              const nextPaymentText = formatRemainingDaysText(daysUntilNextPayment, "el próximo pago");
              const expiryText = formatRemainingDaysText(daysUntilExpiry, "el vencimiento");

              return (
                <Col xs={24} key={licence.id}>
                  <Row gutter={[20, 20]} align="top">
                    <Col xs={24} lg={6}>
                      <CompanyCard licence={licence} />
                    </Col>

                    <Col xs={24} lg={12}>
                      <LicenceCard
                        licence={licence}
                        product={product}
                        payment={payment}
                        status={status}
                        statusProps={statusProps}
                        expiryText={expiryText}
                        nextPaymentText={nextPaymentText}
                        daysUntilExpiry={daysUntilExpiry}
                      />
                    </Col>

                    <Col xs={24} lg={6}>
                      <StatusCard />
                    </Col>
                  </Row>
                </Col>
              );
            })
          )}
        </Row>
      </>
    </WithPermission>
  );
};

export default Licences;